import type { INotification, PlanActionQueueEntryViewType } from '@featit/shared';
import { gql, type GraphqlRequesterType } from './GraphqlClient';
import { workAreaApis } from '@/data-sources/WorkAreaApiRegistry';
import { HOLD_FIELDS, type IWorkItemHold } from './HoldsApi';

/**
 * "Tasks for me" reads (I-0900): the same three sources the backend's `TasksService` unions —
 * responsible items, holds a reader may answer (GV-2e), and open question turns — never a decision,
 * which has its own list (`DecisionApi`). This file only carries the requests; `tasksWaitingForMe` on
 * the server is where the union, the exclusion and the ordering live.
 */

export interface ITaskItemRef {
  id: string;
  workAreaId: string;
  key: string;
  title: string;
  itemType?: { key: string } | null;
  priority?: string | null;
  state?: { name: string; color?: string | null; statusKey?: string | null } | null;
}

/** `notice` is never served: it is an item notice drawn as a row of the list (F-0744, AC-NB4). */
export type TaskWaitReasonType = 'responsible' | 'hold' | 'question' | 'plan_action' | 'notice';

export interface ITask {
  item: ITaskItemRef;
  reason: TaskWaitReasonType;
  reasonLabel: string;
  /** I-2086: every reason the row stands for, in the order its label says them. */
  reasons?: TaskWaitReasonType[];
  /**
   * When the wait on the reader began, from the server (F-0522, §11c TF-25): the handover or last
   * reopen, the hold's filing, the question's asking — never the item's creation.
   */
  waitingSince: string;
  /** Why the row is late — a missed service level or a past target date — or null (TF-29). */
  overdueReason?: string | null;
  /**
   * A `hold` row's hold, answered in the row (T-0492, TF-20) — Tasks for me is where a hold waiting
   * on the reader is answered. Null for the other reasons.
   */
  hold: IWorkItemHold | null;
  /**
   * A `question` row's question: its id and what it asks. A row is one question, so an idea with
   * several waiting on the reader reads as several different rows, and selecting one selects that
   * one. Null for the other reasons.
   */
  questionId?: string | null;
  questionPrompt?: string | null;
  /**
   * F-0535 (HND-7): who handed this step over, why, and which item it holds up — read from the wait on
   * it, so a step an agent filed with a handoff says whose work it is part of. Null for a task
   * nobody handed. `back` (Jonas's day, PX-9): the reader's own item handed back to them, not a step
   * filed and handed to them.
   */
  handedOver?: {
    byName: string;
    reason: string;
    holderKey: string;
    holderTitle: string;
    back?: boolean;
  } | null;
  /**
   * I-1458: the reader's own actions in this item's plan — the active one, then the next few — on a
   * `plan_action` row (the item is somebody else's) or on a `responsible` row whose plan names them.
   */
  planActions?: PlanActionQueueEntryViewType[] | null;
  /**
   * I-1526: the leads this item unblocks, where the reader's action in a lead's plan names it. The
   * wait is this row; the lead is not listed again for it.
   */
  unblocks?: ITaskUnblock[] | null;
  /** F-0744: the notice a `notice` row is, which opening it marks read. Never served. */
  notice?: INotification | null;
}

/** A lead a task's item unblocks through the reader's action in its plan (I-1526). */
export interface ITaskUnblock {
  leadId: string;
  leadKey: string;
  leadTitle: string;
  actionId: string;
  /** T-0707 (PLH-24): the action's first line; the read never selects its text. */
  actionSummary: string;
}

const TASK_ITEM_REF_FIELDS = `
  id
  workAreaId
  key
  title
  itemType { key }
  priority
  state { name color statusKey }
`;

/**
 * PX-9: what Waiting on you reads of a waiting item beyond the task's own ref — whom it is for (its
 * parts), what was promised (its service level's deadline, its target date, its fields), and whether
 * the questions its newest wait waited on were answered since anything else happened on it.
 */
export interface IWaitingTaskItem extends ITaskItemRef {
  targetDate?: string | null;
  lastActivityAt?: string | null;
  fields?: Record<string, unknown> | null;
  parts?: Array<{ name: string; own?: boolean; implied?: boolean }> | null;
  serviceLevel?: { deadline?: { dueAt?: string | null; met?: boolean | null } | null } | null;
  waits?: Array<{
    resolvedAt?: string | null;
    resolvedBy?: { displayName?: string | null } | null;
    questions?: Array<{ id: string }> | null;
  }> | null;
}

/**
 * One task as Waiting on you reads it (PX-9). `checkFlag` (PX-9 d): on the reader's own item, the
 * flag a check of its fields wrote, which waits on them; null when none.
 */
export type WaitingTaskType = Omit<ITask, 'item'> & {
  item: IWaitingTaskItem;
  checkFlag?: string | null;
};

const TASK_FIELDS = `
  item { ${TASK_ITEM_REF_FIELDS} }
  reason
  reasons
  reasonLabel
  waitingSince
  overdueReason
  questionId
  questionPrompt
  handedOver { byName reason holderKey holderTitle back }
  planActions { id summary textBytes truncated state owner active dependsOn { id summary state owner ownerName ownerAvatarUrl } }
  unblocks { leadId leadKey leadTitle actionId actionSummary }
  hold { ${HOLD_FIELDS} }
`;

/**
 * The tasks API over one connection's GraphQL (I-1120): `tasksApiFor(workAreaId)` reaches the deployment that holds the work area; `tasksApi` is the main
 * deployment's.
 */
export function createTasksApi(request: GraphqlRequesterType = gql) {
  return {
    /** One work area's tasks waiting for the reader (I-0900), urgent first then oldest waiting. */
    async waitingForMe(workAreaId: string): Promise<ITask[]> {
      const data = await request<{ tasksWaitingForMe: ITask[] }>(
        `query TasksWaitingForMe($workAreaId: ID!) {
        tasksWaitingForMe(workAreaId: $workAreaId) { ${TASK_FIELDS} }
      }`,
        { workAreaId },
      );
      return data.tasksWaitingForMe;
    },

    /**
     * PX-9: the same list as `waitingForMe`, each item with what Waiting on you's lines read of it
     * (`IWaitingTaskItem`). Its own query, so Tasks for me does not pay for the extra fields.
     */
    async waitingOnYou(workAreaId: string): Promise<WaitingTaskType[]> {
      const data = await request<{ tasksWaitingForMe: WaitingTaskType[] }>(
        `query TasksWaitingForMe($workAreaId: ID!) {
        tasksWaitingForMe(workAreaId: $workAreaId) {
          ${TASK_FIELDS}
          checkFlag
          item {
            ${TASK_ITEM_REF_FIELDS}
            targetDate
            lastActivityAt
            fields
            parts { name own implied }
            serviceLevel { deadline { dueAt met } }
            waits { resolvedAt resolvedBy { displayName } questions { id } }
          }
        }
      }`,
        { workAreaId },
      );
      return data.tasksWaitingForMe;
    },

    /**
     * The same list across every work area this reader may read — the All work areas scope, mirroring
     * `decisionApi.waitingForMeEverywhere` (I-0560): one request, the scope said out loud rather than
     * inferred from an absent work area.
     */
    async waitingForMeEverywhere(): Promise<ITask[]> {
      const data = await request<{ tasksWaitingForMe: ITask[] }>(
        `query TasksWaitingForMeEverywhere($allWorkAreas: Boolean) {
        tasksWaitingForMe(allWorkAreas: $allWorkAreas) { ${TASK_FIELDS} }
      }`,
        { allWorkAreas: true },
      );
      return data.tasksWaitingForMe;
    },

    /**
     * The same list across one namespace's whole subtree (F-0383, §10.9 NV-8), mirroring
     * `decisionApi.waitingForMeInNamespace`.
     */
    async waitingForMeInNamespace(namespaceId: string): Promise<ITask[]> {
      const data = await request<{ tasksWaitingForMe: ITask[] }>(
        `query TasksWaitingForMeInNamespace($namespaceId: ID) {
        tasksWaitingForMe(namespaceId: $namespaceId) { ${TASK_FIELDS} }
      }`,
        { namespaceId },
      );
      return data.tasksWaitingForMe;
    },
  };
}

export type TasksApiType = ReturnType<typeof createTasksApi>;

/** The main deployment's API. A work area's own is `tasksApiFor(workAreaId)`. */
export const tasksApi = createTasksApi();

/** This API for the connection that holds `workAreaId` (I-1120). */
export function tasksApiFor(workAreaId: string | null | undefined): TasksApiType {
  return workAreaApis.api(
    workAreaId,
    'tasksApi',
    (clients) => createTasksApi(clients.gql),
    tasksApi,
  );
}
